/*!
* CoreUI PRO scheduler-core v0.1.0-rc.0 (https://coreui.io)
* Copyright 2026 The CoreUI Team (https://github.com/orgs/coreui/people)
* License (https://coreui.io/pro/license/)
*/
import { Temporal } from "temporal-polyfill";
import * as rrulelib from "rrule";
import { Store } from "@tanstack/store";
//#region src/layout/layout.ts
function layoutColumn(items, options = {}) {
	const { expandToFit = false, minEventMinutes = 0 } = options;
	if (items.length === 0) return [];
	const sorted = items.toSorted((a, b) => a.startMinutes - b.startMinutes || b.endMinutes - b.startMinutes - (a.endMinutes - a.startMinutes) || (a.key < b.key ? -1 : 1));
	const boxes = [];
	let group = [];
	let groupEnd = Number.NEGATIVE_INFINITY;
	const flush = () => {
		if (group.length > 0) {
			boxes.push(...layoutGroup(group, expandToFit));
			group = [];
		}
	};
	for (const entry of sorted) {
		const paddedEnd = Math.max(entry.endMinutes, entry.startMinutes + minEventMinutes);
		if (entry.startMinutes >= groupEnd) {
			flush();
			groupEnd = paddedEnd;
		} else groupEnd = Math.max(groupEnd, paddedEnd);
		group.push({
			...entry,
			paddedEnd,
			track: 0
		});
	}
	flush();
	return boxes;
}
function layoutGroup(group, expandToFit) {
	const trackEnds = [];
	const trackItems = [];
	for (const entry of group) {
		let track = trackEnds.findIndex((end) => end <= entry.startMinutes);
		if (track === -1) {
			track = trackEnds.length;
			trackEnds.push(0);
			trackItems.push([]);
		}
		entry.track = track;
		trackEnds[track] = entry.paddedEnd;
		trackItems[track].push(entry);
	}
	const trackCount = trackEnds.length;
	const trackWidth = 100 / trackCount;
	return group.map((entry) => {
		let span = 1;
		if (expandToFit) while (entry.track + span < trackCount && trackItems[entry.track + span].every((other) => other.paddedEnd <= entry.startMinutes || other.startMinutes >= entry.paddedEnd)) span += 1;
		return {
			key: entry.key,
			startMinutes: entry.startMinutes,
			endMinutes: entry.endMinutes,
			top: entry.startMinutes,
			height: entry.paddedEnd - entry.startMinutes,
			leftPct: entry.track * trackWidth,
			widthPct: trackWidth * span,
			track: entry.track,
			trackCount
		};
	});
}
//#endregion
//#region src/model/time.ts
/**
* --------------------------------------------------------------------------
* CoreUI PRO scheduler-core model/time.ts
* License (https://coreui.io/pro/license/)
* --------------------------------------------------------------------------
* The ONLY ISO/`Date` ⇄ Temporal boundary in the core. Everything past this
* file speaks Temporal; ISO strings and `Date` instances are converted here,
* once, into the scheduler instance's time zone.
*/
const OFFSET_OR_ZULU = /(?:z|[+-]\d{2}(?::?\d{2})?)$/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const MINUTES_IN_DAY = 1440;
function toZoned(value, timeZone) {
	if (value instanceof Date) return Temporal.Instant.fromEpochMilliseconds(value.getTime()).toZonedDateTimeISO(timeZone);
	if (DATE_ONLY.test(value)) return Temporal.PlainDate.from(value).toZonedDateTime({ timeZone });
	if (OFFSET_OR_ZULU.test(value)) return Temporal.Instant.from(value).toZonedDateTimeISO(timeZone);
	return Temporal.PlainDateTime.from(value).toZonedDateTime(timeZone, { disambiguation: "compatible" });
}
function toPlainDate(value, timeZone) {
	if (typeof value === "string" && DATE_ONLY.test(value)) return Temporal.PlainDate.from(value);
	return toZoned(value, timeZone).toPlainDate();
}
/** ISO 8601 with UTC offset, without the IANA suffix — the wire format of every payload. */
function toISO(value) {
	return value.toString({ timeZoneName: "never" });
}
function startOfWeek(date, firstDayOfWeek = 1) {
	return date.subtract({ days: (date.dayOfWeek - firstDayOfWeek + 7) % 7 });
}
/** Wall-clock minutes since the start of the value's day. */
function minutesInDay(value) {
	return value.hour * 60 + value.minute;
}
/**
* The template duration between two instants of the same event, expressed in
* exact time units — RFC 5545 semantics: adding it to a zoned start crosses a
* DST transition without stretching the event's real length.
*/
function exactDuration(start, end) {
	return start.toInstant().until(end.toInstant(), { largestUnit: "hour" });
}
function compare(a, b) {
	return Temporal.ZonedDateTime.compare(a, b);
}
function overlaps(startA, endA, window) {
	return compare(startA, window.end) < 0 && compare(endA, window.start) > 0;
}
function systemTimeZone() {
	return Temporal.Now.timeZoneId();
}
function todayISO(timeZone) {
	return Temporal.Now.zonedDateTimeISO(timeZone).toPlainDate().toString();
}
//#endregion
//#region src/layout/segments.ts
/**
* Clips a timed occurrence into per-day segments over the visible days. The
* grid is a wall-clock 24 h surface, so segment minutes are wall-clock; a
* segment running to the end of a day always closes at 1440.
*/
function segmentIntoDays(occurrence, days, timeZone) {
	const segments = [];
	for (const [dayIndex, day] of days.entries()) {
		const dayStart = day.toZonedDateTime({ timeZone });
		const dayEnd = day.add({ days: 1 }).toZonedDateTime({ timeZone });
		if (compare(occurrence.start, dayEnd) >= 0 || compare(occurrence.end, dayStart) <= 0) continue;
		const clippedStart = compare(occurrence.start, dayStart) > 0 ? occurrence.start : dayStart;
		const clippedEnd = compare(occurrence.end, dayEnd) < 0 ? occurrence.end : dayEnd;
		segments.push({
			dayIndex,
			startMinutes: compare(clippedStart, dayStart) === 0 ? 0 : minutesInDay(clippedStart),
			endMinutes: compare(clippedEnd, dayEnd) === 0 ? MINUTES_IN_DAY : minutesInDay(clippedEnd)
		});
	}
	return segments;
}
//#endregion
//#region src/interaction/machine.ts
/**
* --------------------------------------------------------------------------
* CoreUI PRO scheduler-core interaction/machine.ts
* License (https://coreui.io/pro/license/)
* --------------------------------------------------------------------------
* Drag/select state machine. All spatial reasoning happens in time-space
* (column index + wall-clock minutes); pixels are used only for the press
* threshold. Pointer and keyboard drive the same transitions, so preview,
* commit, and cancel semantics are identical for both by construction.
*/
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
function createInteractionMachine(options = {}) {
	const { snap = 15, dayMinutes = MINUTES_IN_DAY, minMinutes = 0, minDurationMinutes = snap, pressThresholdPx = 3, onChange, onCommit, onClick } = options;
	let columnCount = options.columnCount ?? Number.POSITIVE_INFINITY;
	let state = { type: "idle" };
	let press = null;
	let drag = null;
	const snapRound = (minutes) => Math.round(minutes / snap) * snap;
	const snapFloor = (minutes) => Math.floor(minutes / snap) * snap;
	const snapCeil = (minutes) => Math.ceil(minutes / snap) * snap;
	const clampColumn = (column) => clamp(column, 0, columnCount - 1);
	const setState = (next) => {
		state = next;
		onChange?.(next);
	};
	const reset = () => {
		press = null;
		drag = null;
		setState({ type: "idle" });
	};
	const movePreview = (origin, deltaColumns, rawDeltaMinutes) => {
		const duration = origin.endMinutes - origin.startMinutes;
		const startMinutes = clamp(origin.startMinutes + snapRound(rawDeltaMinutes), minMinutes, dayMinutes - duration);
		return {
			column: clampColumn(origin.column + deltaColumns),
			startMinutes,
			endMinutes: startMinutes + duration
		};
	};
	const resizePreview = (origin, handle, rawDeltaMinutes) => {
		if (handle === "end") {
			const endMinutes = clamp(snapRound(origin.endMinutes + rawDeltaMinutes), origin.startMinutes + minDurationMinutes, dayMinutes);
			return {
				column: origin.column,
				startMinutes: origin.startMinutes,
				endMinutes
			};
		}
		const startMinutes = clamp(snapRound(origin.startMinutes + rawDeltaMinutes), minMinutes, origin.endMinutes - minDurationMinutes);
		return {
			column: origin.column,
			startMinutes,
			endMinutes: origin.endMinutes
		};
	};
	const selectionRange = (anchor, focus) => {
		const forward = anchor.column < focus.column || anchor.column === focus.column && anchor.minutes <= focus.minutes;
		const from = forward ? anchor : focus;
		const to = forward ? focus : anchor;
		const startMinutes = snapFloor(from.minutes);
		let endMinutes = snapCeil(to.minutes);
		if (from.column === to.column && endMinutes <= startMinutes) endMinutes = startMinutes + snap;
		return {
			startColumn: clampColumn(from.column),
			startMinutes: clamp(startMinutes, minMinutes, dayMinutes - snap),
			endColumn: clampColumn(to.column),
			endMinutes: clamp(endMinutes, minMinutes + snap, dayMinutes)
		};
	};
	const updateDrag = (pointer) => {
		if (!press) return;
		const rawDeltaMinutes = pointer.point.minutes - press.pointer.point.minutes;
		const deltaColumns = pointer.point.column - press.pointer.point.column;
		if (drag) {
			if (drag.mode === "move") setState({
				type: "drag-move",
				occurrenceKey: drag.occurrenceKey,
				preview: movePreview(drag.origin, deltaColumns, rawDeltaMinutes)
			});
			else {
				const handle = drag.mode === "resize-start" ? "start" : "end";
				setState({
					type: "drag-resize",
					occurrenceKey: drag.occurrenceKey,
					handle,
					preview: resizePreview(drag.origin, handle, rawDeltaMinutes)
				});
			}
			return;
		}
		setState({
			type: "select-range",
			range: selectionRange(press.pointer.point, pointer.point)
		});
	};
	const commitState = () => {
		try {
			if (state.type === "drag-move" && drag) onCommit?.({
				action: "move",
				occurrenceKey: state.occurrenceKey,
				deltaColumns: state.preview.column - drag.origin.column,
				deltaMinutes: state.preview.startMinutes - drag.origin.startMinutes,
				preview: state.preview
			});
			else if (state.type === "drag-resize") onCommit?.({
				action: "resize",
				occurrenceKey: state.occurrenceKey,
				handle: state.handle,
				preview: state.preview
			});
			else if (state.type === "select-range") onCommit?.({
				action: "select",
				range: state.range
			});
		} finally {
			reset();
		}
	};
	return {
		getState: () => state,
		setColumnCount(count) {
			columnCount = count;
		},
		pointerDown(pointer, target = {}) {
			press = {
				pointer,
				target
			};
			drag = null;
			setState({ type: "pressed" });
		},
		pointerMove(pointer) {
			if (!press) return;
			if (state.type === "pressed") {
				const dx = pointer.pixel.x - press.pointer.pixel.x;
				const dy = pointer.pixel.y - press.pointer.pixel.y;
				if (Math.hypot(dx, dy) < pressThresholdPx) return;
				const { occurrenceKey, handle, origin } = press.target;
				if (occurrenceKey && origin) drag = {
					occurrenceKey,
					origin,
					mode: handle ? handle === "start" ? "resize-start" : "resize-end" : "move"
				};
			}
			updateDrag(pointer);
		},
		pointerUp() {
			if (state.type === "pressed" && press) {
				const { occurrenceKey } = press.target;
				reset();
				onClick?.({ occurrenceKey });
				return;
			}
			commitState();
		},
		grab(occurrenceKey, origin, mode = "move") {
			drag = {
				occurrenceKey,
				origin: {
					...origin,
					column: clampColumn(origin.column)
				},
				mode
			};
			press = null;
			if (mode === "move") setState({
				type: "drag-move",
				occurrenceKey,
				preview: movePreview(drag.origin, 0, 0)
			});
			else {
				const handle = mode === "resize-start" ? "start" : "end";
				setState({
					type: "drag-resize",
					occurrenceKey,
					handle,
					preview: resizePreview(drag.origin, handle, 0)
				});
			}
		},
		nudge(delta) {
			if (!drag || state.type !== "drag-move" && state.type !== "drag-resize") return;
			const { preview } = state;
			const minutes = delta.minutes ?? 0;
			const columns = delta.columns ?? 0;
			if (state.type === "drag-move") {
				const duration = preview.endMinutes - preview.startMinutes;
				const startMinutes = clamp(preview.startMinutes + minutes, minMinutes, dayMinutes - duration);
				setState({
					type: "drag-move",
					occurrenceKey: state.occurrenceKey,
					preview: {
						column: clampColumn(preview.column + columns),
						startMinutes,
						endMinutes: startMinutes + duration
					}
				});
				return;
			}
			const next = state.handle === "end" ? {
				...preview,
				endMinutes: clamp(preview.endMinutes + minutes, preview.startMinutes + minDurationMinutes, dayMinutes)
			} : {
				...preview,
				startMinutes: clamp(preview.startMinutes + minutes, minMinutes, preview.endMinutes - minDurationMinutes)
			};
			setState({
				type: "drag-resize",
				occurrenceKey: state.occurrenceKey,
				handle: state.handle,
				preview: next
			});
		},
		commit() {
			commitState();
		},
		cancel() {
			reset();
		}
	};
}
//#endregion
//#region src/keyboard/navigation.ts
function resolveNavigation(position, key, bounds) {
	const clampColumn = (column) => Math.min(Math.max(column, 0), bounds.columns - 1);
	const clampSlot = (slot) => Math.min(Math.max(slot, 0), bounds.slots - 1);
	switch (key) {
		case "up": return {
			...position,
			slot: clampSlot(position.slot - 1)
		};
		case "down": return {
			...position,
			slot: clampSlot(position.slot + 1)
		};
		case "left": return {
			...position,
			column: clampColumn(position.column - 1)
		};
		case "right": return {
			...position,
			column: clampColumn(position.column + 1)
		};
		case "home": return {
			...position,
			slot: 0
		};
		case "end": return {
			...position,
			slot: bounds.slots - 1
		};
		default: return position;
	}
}
//#endregion
//#region src/labels.ts
const DEFAULT_LABELS = {
	agenda: "Agenda",
	agendaEmpty: "No events in this range",
	allDay: "All-day",
	cancel: "Cancel",
	colorLabel: "Color",
	day: "Day",
	deleteEvent: "Delete",
	editEvent: "Edit event",
	editScope: "Applies to",
	endLabel: "End",
	eventCancelled: "Cancelled",
	eventCreated: "Created {title}, {date} {start} to {end}",
	eventDeleted: "{title} deleted",
	eventGrabbed: "{title} grabbed. Use the arrow keys to move, Enter to confirm, Escape to cancel",
	eventMoved: "{title} moved to {date} {start}",
	eventResized: "{title} now lasts from {start} to {end}",
	eventUpdated: "{title} updated",
	loadFailed: "Failed to load events",
	month: "Month",
	more: "+{count} more",
	newEvent: "New event",
	next: "Next",
	outsideBusinessHours: "Outside business hours",
	overlapBlocked: "That time is already occupied",
	prev: "Previous",
	repeat: "Repeat",
	repeatDaily: "Daily",
	repeatMonthly: "Monthly",
	repeatNone: "Does not repeat",
	repeatWeekly: "Weekly",
	resource: "Resources",
	save: "Save",
	saveFailed: "The change could not be saved",
	scopeAll: "All events",
	scopeFuture: "This and following events",
	scopeOccurrence: "This event",
	startLabel: "Start",
	timeline: "Timeline",
	titleLabel: "Title",
	today: "Today",
	views: "Views",
	week: "Week"
};
function interpolate(template, values) {
	return template.replace(/\{(\w+)\}/g, (match, token) => values[token] ?? match);
}
//#endregion
//#region src/recurrence/expand.ts
/**
* --------------------------------------------------------------------------
* CoreUI PRO scheduler-core recurrence/expand.ts
* License (https://coreui.io/pro/license/)
* --------------------------------------------------------------------------
* The ONLY seam where rrule meets Temporal. rrule expands in floating time
* (UTC-flagged dates carrying wall-clock digits); each occurrence's wall
* clock is then resolved to the instance time zone with
* `disambiguation: 'compatible'`, and occurrence ends are start + the
* template's exact duration (RFC 5545). rrule's own `tzid` is never used.
*/
const { RRule } = rrulelib.default ?? rrulelib;
const CACHE_LIMIT = 500;
const cache = /* @__PURE__ */ new Map();
function expandEvent(event, window, timeZone) {
	const cacheKey = `${JSON.stringify(event)}|${timeZone}|${window.start.epochMilliseconds}|${window.end.epochMilliseconds}`;
	const cached = cache.get(cacheKey);
	if (cached) return cached;
	const start = toZoned(event.start, timeZone);
	const end = toZoned(event.end, timeZone);
	const occurrences = event.rrule ? expandRecurring(event, start, end, window, timeZone) : overlaps(start, end, window) ? [makeOccurrence(event, start, end, false)] : [];
	if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value);
	cache.set(cacheKey, occurrences);
	return occurrences;
}
function expandEvents(events, window, timeZone) {
	return events.flatMap((event) => expandEvent(event, window, timeZone)).toSorted((a, b) => Temporal.ZonedDateTime.compare(a.start, b.start) || (a.key < b.key ? -1 : 1));
}
function expandRecurring(event, start, end, window, timeZone) {
	const duration = exactDuration(start, end);
	const rule = parseRule(event.rrule, toFloating(start));
	const lookbehind = window.start.toInstant().subtract(duration).toZonedDateTimeISO(timeZone);
	const floatingOccurrences = rule.between(toFloating(lookbehind), toFloating(window.end), true);
	const excluded = new Set((event.exdates ?? []).map((exdate) => toZoned(exdate, timeZone).toPlainDateTime().toString()));
	const occurrences = [];
	for (const floating of floatingOccurrences) {
		const occurrenceStart = fromFloating(floating, timeZone);
		if (excluded.has(occurrenceStart.toPlainDateTime().toString())) continue;
		const occurrenceEnd = occurrenceStart.add(duration);
		if (overlaps(occurrenceStart, occurrenceEnd, window)) occurrences.push(makeOccurrence(event, occurrenceStart, occurrenceEnd, true));
	}
	return occurrences;
}
function makeOccurrence(event, start, end, recurring) {
	return {
		event,
		key: `${event.id}@${start.toString({ timeZoneName: "never" })}`,
		start,
		end,
		allDay: Boolean(event.allDay),
		recurring
	};
}
function parseRule(rrule, dtstart) {
	const options = RRule.parseString(rrule.replace(/^RRULE:/i, "").trim());
	options.dtstart = dtstart;
	return new RRule(options);
}
/** Wall-clock digits re-labelled as UTC — the floating-time convention rrule expects. */
function toFloating(value) {
	return new Date(Date.UTC(value.year, value.month - 1, value.day, value.hour, value.minute, value.second));
}
function fromFloating(value, timeZone) {
	return new Temporal.PlainDateTime(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate(), value.getUTCHours(), value.getUTCMinutes(), value.getUTCSeconds()).toZonedDateTime(timeZone, { disambiguation: "compatible" });
}
//#endregion
//#region src/state/store.ts
/**
* --------------------------------------------------------------------------
* CoreUI PRO scheduler-core state/store.ts
* License (https://coreui.io/pro/license/)
* --------------------------------------------------------------------------
*/
function createSchedulerStore(options = {}) {
	const timeZone = options.timeZone ?? systemTimeZone();
	const store = new Store({
		date: options.date ? toPlainDate(options.date, timeZone).toString() : todayISO(timeZone),
		events: options.events ? [...options.events] : [],
		resources: options.resources ? [...options.resources] : [],
		selection: [],
		timeZone,
		view: options.view ?? "week"
	});
	const patch = (partial) => {
		store.setState((state) => ({
			...state,
			...partial
		}));
	};
	return {
		store,
		setDate(date) {
			patch({ date: toPlainDate(date, store.state.timeZone).toString() });
		},
		setView(view) {
			patch({ view });
		},
		navigate(direction, stepDays) {
			if (direction === "today") {
				patch({ date: todayISO(store.state.timeZone) });
				return;
			}
			const { view } = store.state;
			const sign = direction === "next" ? 1 : -1;
			const anchor = toPlainDate(store.state.date, store.state.timeZone);
			if (view === "month") {
				patch({ date: anchor.add({ months: sign }).toString() });
				return;
			}
			const days = stepDays ?? (view === "week" ? 7 : view === "agenda" ? 14 : 1);
			patch({ date: anchor.add({ days: days * sign }).toString() });
		},
		setEvents(events) {
			patch({ events: [...events] });
		},
		setResources(resources) {
			patch({ resources: [...resources] });
		},
		setSelection(selection) {
			patch({ selection: [...selection] });
		},
		/** Applies an event change and returns a revert function for `eventChange` payloads. */
		applyPatch(change) {
			const previous = store.state.events;
			const scope = change.scope ?? "occurrence";
			if (change.action === "create") patch({ events: [...previous, change.event] });
			else if (change.action === "delete") patch({ events: deleteEvents(previous, change, scope, store.state.timeZone) });
			else if (change.event.rrule && change.occurrenceKey) {
				if (scope === "future") patch({ events: splitSeries(previous, change, store.state.timeZone) });
				else if (scope === "all") patch({ events: shiftSeries(previous, change, store.state.timeZone) });
				else patch({ events: detachOccurrence(previous, change) });
			} else patch({ events: previous.map((event) => event.id === change.event.id ? change.event : event) });
			return () => patch({ events: previous });
		}
	};
}
function excludeOccurrence(events, change) {
	const occurrenceStart = change.occurrenceKey.slice(change.event.id.length + 1);
	return events.map((event) => event.id === change.event.id ? {
		...event,
		exdates: [...event.exdates ?? [], occurrenceStart]
	} : event);
}
/**
* "This occurrence only" edit: the series gains an EXDATE for the dragged
* occurrence and the change materializes as a standalone one-off event.
*/
function detachOccurrence(events, change) {
	const occurrenceStart = change.occurrenceKey.slice(change.event.id.length + 1);
	const detached = {
		...change.event,
		id: `${change.event.id}@${occurrenceStart}`,
		rrule: void 0,
		exdates: void 0
	};
	return [...events.map((event) => event.id === change.event.id ? {
		...event,
		exdates: [...event.exdates ?? [], occurrenceStart]
	} : event), detached];
}
function deleteEvents(events, change, scope, timeZone) {
	if (!change.event.rrule || !change.occurrenceKey || scope === "all") return events.filter((event) => event.id !== change.event.id);
	if (scope === "future") return truncateSeries(events, change, timeZone);
	return excludeOccurrence(events, change);
}
const pad = (value, length = 2) => String(value).padStart(length, "0");
const occurrenceStartOf = (change) => change.occurrenceKey.slice(change.event.id.length + 1);
const stripRuleParts = (rrule, parts) => rrule.replace(/^RRULE:/i, "").split(";").filter((entry) => !parts.includes(entry.split("=")[0].toUpperCase())).join(";");
/** Floating RRULE UNTIL just before the given wall-clock instant. */
const untilBefore = (occurrenceStart, timeZone) => {
	const boundary = toZoned(occurrenceStart, timeZone).toPlainDateTime().subtract({ seconds: 1 });
	return `${pad(boundary.year, 4)}${pad(boundary.month)}${pad(boundary.day)}T${pad(boundary.hour)}${pad(boundary.minute)}${pad(boundary.second)}`;
};
/** Ends the original series just before the edited occurrence. */
function truncateSeries(events, change, timeZone) {
	const occurrenceStart = occurrenceStartOf(change);
	return events.map((event) => event.id === change.event.id ? {
		...event,
		rrule: `${stripRuleParts(event.rrule, ["UNTIL", "COUNT"])};UNTIL=${untilBefore(occurrenceStart, timeZone)}`
	} : event);
}
/**
* "This and future": the original series ends just before the edited
* occurrence and the remainder becomes a new series starting at the edited
* time. COUNT is dropped on split and the new series starts with no
* exdates — documented limitations until the full RRULE editor.
*/
function splitSeries(events, change, timeZone) {
	const occurrenceStart = occurrenceStartOf(change);
	const remainder = {
		...change.event,
		id: `${change.event.id}@${occurrenceStart}`,
		rrule: stripRuleParts(change.event.rrule, ["COUNT"]),
		exdates: void 0
	};
	return [...truncateSeries(events, change, timeZone), remainder];
}
/** Applies the edit's wall-clock delta to the whole series. */
function shiftSeries(events, change, timeZone) {
	const occurrenceStart = toZoned(occurrenceStartOf(change), timeZone).toPlainDateTime();
	const newStart = toZoned(change.event.start, timeZone).toPlainDateTime();
	const delta = occurrenceStart.until(newStart, { largestUnit: "hour" });
	const newDuration = toZoned(change.event.start, timeZone).toPlainDateTime().until(toZoned(change.event.end, timeZone).toPlainDateTime(), { largestUnit: "hour" });
	return events.map((event) => {
		if (event.id !== change.event.id) return event;
		const seriesStart = toZoned(event.start, timeZone).toPlainDateTime().add(delta);
		return {
			...change.event,
			id: event.id,
			start: seriesStart.toString(),
			end: seriesStart.add(newDuration).toString()
		};
	});
}
//#endregion
//#region src/state/view-model.ts
function getVisibleDays(state, firstDayOfWeek = 1) {
	const anchor = toPlainDate(state.date, state.timeZone);
	if (state.view !== "week") return [anchor];
	const first = startOfWeek(anchor, firstDayOfWeek);
	return Array.from({ length: 7 }, (_, index) => first.add({ days: index }));
}
function computeViewModel(state, options = {}) {
	const { firstDayOfWeek = 1, dayStartMinutes = 0, dayEndMinutes = MINUTES_IN_DAY, ...layoutOptions } = options;
	const { timeZone } = state;
	const days = getVisibleDays(state, firstDayOfWeek);
	const window = {
		start: days[0].toZonedDateTime({ timeZone }),
		end: days[days.length - 1].add({ days: 1 }).toZonedDateTime({ timeZone })
	};
	const columns = state.view === "resource" ? state.resources.map((resource) => ({
		key: `${days[0].toString()}|${resource.id}`,
		date: days[0],
		dateISO: days[0].toString(),
		resourceId: resource.id,
		label: resource.label ?? resource.id
	})) : days.map((day) => ({
		key: day.toString(),
		date: day,
		dateISO: day.toString()
	}));
	const occurrences = expandEvents(state.events, window, timeZone);
	const timed = occurrences.filter((occurrence) => !occurrence.allDay);
	const allDay = occurrences.filter((occurrence) => occurrence.allDay);
	const columnItems = columns.map(() => []);
	const itemMeta = /* @__PURE__ */ new Map();
	for (const occurrence of timed) {
		const inColumns = state.view === "resource" ? columns.flatMap((column, index) => column.resourceId === occurrence.event.resourceId ? [index] : []) : null;
		if (state.view === "resource" && inColumns?.length === 0) continue;
		const segments = segmentIntoDays(occurrence, days, timeZone);
		for (const [segmentIndex, segment] of segments.entries()) {
			const startMinutes = Math.max(segment.startMinutes, dayStartMinutes);
			const endMinutes = Math.min(segment.endMinutes, dayEndMinutes);
			if (startMinutes >= endMinutes) continue;
			const columnIndex = state.view === "resource" ? inColumns[0] : segment.dayIndex;
			const key = `${occurrence.key}#${segment.dayIndex}`;
			columnItems[columnIndex].push({
				key,
				startMinutes,
				endMinutes
			});
			itemMeta.set(key, {
				occurrence,
				continuesBefore: segmentIndex > 0,
				continuesAfter: segmentIndex < segments.length - 1
			});
		}
	}
	return {
		days,
		columns,
		window,
		occurrences,
		columnLayouts: columns.map((column, index) => ({
			column,
			boxes: layoutColumn(columnItems[index], layoutOptions).map((box) => {
				const meta = itemMeta.get(box.key);
				return {
					...box,
					...meta
				};
			}),
			allDay: allDay.filter((occurrence) => {
				if (state.view === "resource" && occurrence.event.resourceId !== column.resourceId) return false;
				const dayWindow = {
					start: column.date.toZonedDateTime({ timeZone }),
					end: column.date.add({ days: 1 }).toZonedDateTime({ timeZone })
				};
				return overlaps(occurrence.start, occurrence.end, dayWindow);
			})
		}))
	};
}
function getMonthDays(state, firstDayOfWeek = 1) {
	const anchor = toPlainDate(state.date, state.timeZone);
	const first = startOfWeek(anchor.with({ day: 1 }), firstDayOfWeek);
	const gridEnd = startOfWeek(anchor.with({ day: anchor.daysInMonth }), firstDayOfWeek).add({ days: 7 });
	const length = first.until(gridEnd).total({ unit: "days" });
	return Array.from({ length }, (_, index) => first.add({ days: index }));
}
function getAgendaDays(state, agendaDays = 14) {
	const anchor = toPlainDate(state.date, state.timeZone);
	return Array.from({ length: agendaDays }, (_, index) => anchor.add({ days: index }));
}
/** Per-day occurrence lists for the month and agenda views (all-day first, then by start). */
function computeDayListModel(state, days) {
	const { timeZone } = state;
	const window = {
		start: days[0].toZonedDateTime({ timeZone }),
		end: days[days.length - 1].add({ days: 1 }).toZonedDateTime({ timeZone })
	};
	const occurrences = expandEvents(state.events, window, timeZone);
	return days.map((date) => {
		const dayWindow = {
			start: date.toZonedDateTime({ timeZone }),
			end: date.add({ days: 1 }).toZonedDateTime({ timeZone })
		};
		return {
			date,
			dateISO: date.toString(),
			occurrences: occurrences.filter((occurrence) => overlaps(occurrence.start, occurrence.end, dayWindow)).toSorted((a, b) => Number(b.allDay) - Number(a.allDay))
		};
	});
}
/**
* Horizontal resource-timeline model: one row per resource, bars measured in
* wall-clock minute offsets from the window start (continuous across days —
* midnight-crossing bars are NOT split), overlaps stacked into sub-lanes by
* the same interval-partitioning layout the day columns use.
*/
function computeTimelineModel(state, days, layoutOptions = {}) {
	const { timeZone } = state;
	const totalMinutes = days.length * MINUTES_IN_DAY;
	const window = {
		start: days[0].toZonedDateTime({ timeZone }),
		end: days[days.length - 1].add({ days: 1 }).toZonedDateTime({ timeZone })
	};
	const occurrences = expandEvents(state.events, window, timeZone);
	const offsetOf = (value) => {
		return days[0].until(value.toPlainDate()).total({ unit: "days" }) * MINUTES_IN_DAY + minutesInDay(value);
	};
	return {
		days,
		window,
		occurrences,
		rows: state.resources.map((resource) => {
			const itemMeta = /* @__PURE__ */ new Map();
			const items = [];
			for (const occurrence of occurrences) {
				if (occurrence.event.resourceId !== resource.id) continue;
				const rawStart = occurrence.allDay ? days[0].until(occurrence.start.toPlainDate()).total({ unit: "days" }) * MINUTES_IN_DAY : offsetOf(occurrence.start);
				const rawEnd = occurrence.allDay ? days[0].until(occurrence.end.toPlainDate()).total({ unit: "days" }) * MINUTES_IN_DAY : offsetOf(occurrence.end);
				const startMinutes = Math.max(rawStart, 0);
				const endMinutes = Math.min(rawEnd, totalMinutes);
				if (startMinutes >= endMinutes) continue;
				const key = `${occurrence.key}#t`;
				items.push({
					key,
					startMinutes,
					endMinutes
				});
				itemMeta.set(key, {
					occurrence,
					continuesBefore: rawStart < 0,
					continuesAfter: rawEnd > totalMinutes
				});
			}
			const boxes = layoutColumn(items, layoutOptions).map((box) => ({
				...box,
				...itemMeta.get(box.key)
			}));
			return {
				resource,
				boxes,
				laneCount: Math.max(1, ...boxes.map((box) => box.track + 1))
			};
		}),
		totalMinutes
	};
}
/**
* Month-grid model with week-spanning bars: occurrences covering more than
* one day render as bars per week row (lane-stacked, clipped at week edges
* with continuation flags); single-day occurrences stay in their day's chip
* list.
*/
function computeMonthModel(state, days) {
	const { timeZone } = state;
	const window = {
		start: days[0].toZonedDateTime({ timeZone }),
		end: days[days.length - 1].add({ days: 1 }).toZonedDateTime({ timeZone })
	};
	const occurrences = expandEvents(state.events, window, timeZone);
	const lastIndex = days.length - 1;
	const span = (occurrence) => {
		const firstDate = occurrence.start.toPlainDate();
		const lastDate = occurrence.allDay ? occurrence.end.toPlainDate().subtract({ days: 1 }) : occurrence.end.subtract({ minutes: 1 }).toPlainDate();
		return {
			first: days[0].until(firstDate).total({ unit: "days" }),
			last: days[0].until(lastDate).total({ unit: "days" })
		};
	};
	const multiDay = occurrences.filter((occurrence) => {
		const { first, last } = span(occurrence);
		return last > first;
	});
	const singles = occurrences.filter((occurrence) => !multiDay.includes(occurrence));
	const dayLists = days.map((date) => {
		const dayWindow = {
			start: date.toZonedDateTime({ timeZone }),
			end: date.add({ days: 1 }).toZonedDateTime({ timeZone })
		};
		return {
			date,
			dateISO: date.toString(),
			occurrences: singles.filter((occurrence) => overlaps(occurrence.start, occurrence.end, dayWindow)).toSorted((a, b) => Number(b.allDay) - Number(a.allDay))
		};
	});
	const weeks = [];
	for (let weekStart = 0; weekStart <= lastIndex; weekStart += 7) {
		const weekEnd = Math.min(weekStart + 6, lastIndex);
		const items = [];
		const meta = /* @__PURE__ */ new Map();
		for (const occurrence of multiDay) {
			const { first, last } = span(occurrence);
			if (last < weekStart || first > weekEnd) continue;
			const startCol = Math.max(first - weekStart, 0);
			const endCol = Math.min(last - weekStart, weekEnd - weekStart);
			const key = `${occurrence.key}#w${weekStart}`;
			items.push({
				key,
				startMinutes: startCol,
				endMinutes: endCol + 1
			});
			meta.set(key, {
				key,
				occurrence,
				startCol,
				endCol,
				lane: 0,
				continuesBefore: first < weekStart,
				continuesAfter: last > weekEnd
			});
		}
		const boxes = layoutColumn(items);
		const bars = boxes.map((box) => ({
			...meta.get(box.key),
			lane: box.track
		}));
		weeks.push({
			days: dayLists.slice(weekStart, weekEnd + 1),
			bars,
			laneCount: Math.max(0, ...boxes.map((box) => box.track + 1))
		});
	}
	return {
		days,
		weeks,
		occurrences
	};
}
//#endregion
export { DEFAULT_LABELS, MINUTES_IN_DAY, compare, computeDayListModel, computeMonthModel, computeTimelineModel, computeViewModel, createInteractionMachine, createSchedulerStore, exactDuration, expandEvent, expandEvents, getAgendaDays, getMonthDays, getVisibleDays, interpolate, layoutColumn, minutesInDay, overlaps, resolveNavigation, segmentIntoDays, startOfWeek, systemTimeZone, toISO, toPlainDate, toZoned, todayISO };

//# sourceMappingURL=index.mjs.map